import axios from "axios";
import {
  IS_LOGIN,
  PERSIST_AUTH,
  SHOW_LOADING,
  HIDE_LOADING,
  LOGIN_ERROR,
  CLEAR_LOGIN_ERROR,
  IS_LOGOUT,
  WEBAUTHN_PREAUTH,
  WEBAUTHN_FLOW_RESET,
} from "./types";
import { MAIN_API } from "../../config/stepoutAPI";
import { trackEvent } from "../../utils/mixpanel";
import { identifyGA, resetGA } from "../../utils/ga";
import { signFlag } from "../../utils/secureFlag";
import {
  WEBAUTHN_ENABLED,
  WEBAUTHN_MOCK,
  NEXT_STEP,
  WEBAUTHN_BYPASS_ANALYST_IDS,
} from "../../features/webauthn/config";
import { getRegRequestId, clearRegRequestId } from "../../features/webauthn/regRequestStore";

/**
 * Persist an authenticated session from a login-style `response_data` payload
 * and flip Redux to logged-in. Shared by the legacy password login and the
 * WebAuthn second-factor completion — both end here so there is exactly one
 * place a session is established (mirrors the spec's single `login()` call).
 */
export const applyAuthResponse = (dispatch, response_data) => {
  const _token = response_data.token;
  // The WebAuthn verify payload is leaner than legacy login (no name/role/tabs
  // guaranteed), so default the optional profile fields rather than writing
  // "undefined" into sessionStorage.
  const analystName = response_data.analyst_name ?? "";
  const isAdmin = !!response_data.is_admin;
  const isSuperAdmin = !!response_data.is_super_admin;
  const role = response_data.role ?? null;
  const tabs = response_data.tabs_accessible ?? null;

  console.log(response_data, "response_data");

  // Authorization flags are signed and bound to the token so they can't
  // be edited directly in sessionStorage (see utils/secureFlag).
  sessionStorage.setItem("token", _token);
  sessionStorage.setItem("analyst_id", response_data.analyst_id ?? "");
  sessionStorage.setItem("analystName", analystName);
  sessionStorage.setItem("isAdmin", signFlag({ status: isAdmin }, _token));
  sessionStorage.setItem("isSuperAdmin", signFlag({ status: isSuperAdmin }, _token));
  sessionStorage.setItem("accountRole", signFlag(role, _token));
  sessionStorage.setItem("refresh_token", response_data.refresh_token ?? "");
  sessionStorage.setItem("tabsAccessible", signFlag({ tabs }, _token));

  dispatch({
    type: IS_LOGIN,
    payload: response_data.analyst_id,
    isAdmin,
    isSuperAdmin,
    token: _token,
    analystName,
    accountRole: role,
    tabsAccessible: tabs,
  });

  // Mixpanel – identify user and track successful login
  trackEvent().init(response_data.analyst_id, analystName);
  trackEvent().track("clicked_on_login", {
    user_name: analystName,
    user_id: response_data.analyst_id,
    user_type: role,
  });

  // Google Analytics – attribute subsequent events to this user.
  identifyGA({ userId: response_data.analyst_id, name: analystName, role });
};

/**
 * Enter the WebAuthn pre-auth state after a correct password. No token is
 * stored yet — the flow UI (see features/webauthn) drives the ceremony and
 * calls finalizeWebauthnLogin() on success.
 */
export const enterWebauthnFlow = ({
  next_step,
  preauth_ticket,
  request_id,
  analyst_id,
  is_admin,
  analyst_name,
  role,
  tabs_accessible,
  force_enroll,
}) => ({
  type: WEBAUTHN_PREAUTH,
  next_step,
  preauth_ticket,
  request_id,
  // Profile fields the login step returns but the verify response does not —
  // carried here so finalizeWebauthnLogin can merge them into the session.
  analyst_id,
  is_admin,
  analyst_name,
  role,
  tabs_accessible,
  // When true, EnrollScreen skips the status re-check and shows the request
  // form directly (used by "Register again" after a rejection).
  force_enroll,
});

export const resetWebauthnFlow = () => ({ type: WEBAUTHN_FLOW_RESET });

/**
 * Complete login from a WebAuthn verify/enroll response that carries a token.
 * The verify payload is lean (token, analyst_id, refresh_token); is_admin comes
 * from the earlier login step held in pre-auth state, so merge it in.
 */
export const finalizeWebauthnLogin = (response_data) => async (dispatch, getState) => {
  const pre = getState().auth.webauthn || {};
  const merged = {
    is_admin: pre.is_admin,
    analyst_id: pre.analyst_id,
    analyst_name: pre.analyst_name,
    role: pre.role,
    tabs_accessible: pre.tabs_accessible,
    // verify payload (token, refresh_token, analyst_id, …) wins on overlap.
    ...response_data,
  };
  // Enrollment/auth is complete — the request_id is consumed and must never be
  // reused. Drop the per-browser stored id so a later revoke → re-request
  // starts fresh.
  clearRegRequestId(pre.analyst_id);
  applyAuthResponse(dispatch, merged);
  dispatch({ type: WEBAUTHN_FLOW_RESET });
};

export const LoginUser = (username, password) => {
  const formData = new FormData();
  formData.append("username", username);
  formData.append("password", password);

  return async (dispatch) => {
    dispatch({ type: CLEAR_LOGIN_ERROR });
    dispatch({ type: SHOW_LOADING });
    try {
      let response_data;
      if (WEBAUTHN_ENABLED && WEBAUTHN_MOCK) {
        // Demo path: skip the network, ask the mock what the next step is.
        response_data = await require("../../features/webauthn/api/mock").mockApi.login(
          username,
          password
        );
      } else {
        const api_res = await axios.post(MAIN_API + "loginAnalyst/v2/", formData);
        response_data = (await api_res.data).response_data;
      }

      if (!response_data || Object.keys(response_data).length === 0) return;

      // Allowlisted analysts skip the second factor entirely (testing).
      const bypassed = WEBAUTHN_BYPASS_ANALYST_IDS.includes(Number(response_data.analyst_id));

      // WebAuthn enforced: password OK but not yet authenticated — branch to
      // the second-factor flow instead of finalizing a session. The signal is a
      // `next_step` (with no token); the API does not send an `authenticated`
      // flag.
      if (
        WEBAUTHN_ENABLED &&
        !bypassed &&
        response_data.next_step &&
        response_data.next_step !== NEXT_STEP.AUTHENTICATED &&
        !response_data.token
      ) {
        dispatch({ type: HIDE_LOADING });
        dispatch(
          enterWebauthnFlow({
            next_step: response_data.next_step,
            preauth_ticket: response_data.preauth_ticket,
            // Seed ONLY from this browser's persisted request — never adopt the
            // server-sent request_id. The login response can carry an
            // account-scoped registration_ready + request_id that does not
            // belong to this device; trusting it wrongly shows "Finish setup"
            // on a device that never enrolled.
            request_id: getRegRequestId(response_data.analyst_id),
            analyst_id: response_data.analyst_id,
            is_admin: response_data.is_admin,
            analyst_name: response_data.analyst_name,
            role: response_data.role,
            tabs_accessible: response_data.tabs_accessible,
          })
        );
        return;
      }

      // Legacy / flag-off / authenticated: finalize exactly as before. Never
      // authenticate without a token (guards against a missing-token response).
      if (response_data.token) {
        applyAuthResponse(dispatch, response_data);
      } else {
        dispatch({ type: HIDE_LOADING });
        dispatch({ type: LOGIN_ERROR });
      }
    } catch (error) {
      dispatch({ type: HIDE_LOADING });
      dispatch({ type: LOGIN_ERROR });
      console.log("wrong credential", error.data);
    }
  };
};

export const LogoutUser = (queryClient) => {
  queryClient && queryClient.removeQueries();
  return async (dispatch) => {
    sessionStorage.clear();
    sessionStorage.removeItem("analyst_id");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("all_matches");
    // Wipe locally-persisted IndexedDB data so the next user on a shared
    // machine can't read this analyst's sessions/strings.
    try {
      const { clearAllPersistence } = await import(
        "../../persistence/db/database"
      );
      await clearAllPersistence();
    } catch (e) {
      console.warn("[logout] local data wipe failed:", e?.message);
    }
    resetGA();
    dispatch({ type: IS_LOGOUT });
  };
};

export const CheckPersist = () => {
  return {
    type: PERSIST_AUTH,
  };
};
